import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";

const SALUTATIONS = ["Mr.", "Mrs.", "Miss", "Dr.", "M/s"];

const EMPTY_FORM = {
  salutation:       "",
  name:             "",
  additionalDetail: "",
  contact:          "",
  email:            "",
  taxName:          "",
  taxNumber:        "",
  address:          "",
  openingBalance:   "",
  openingDate:      new Date().toISOString().split("T")[0],
  balanceType:      "you_owe",
};

const AddVendorModal = ({ existingVendor, onSave, onClose }) => {
  const { t } = useTranslation("pages");
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const isEdit = !!existingVendor;

  useEffect(() => {
    if (existingVendor) setForm({ ...EMPTY_FORM, ...existingVendor });
    else setForm(EMPTY_FORM);
  }, [existingVendor]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("vendorModal.validation.nameRequired");
    if (form.email && !/^.+@.+\..+$/.test(form.email))
      e.email = t("vendorModal.validation.emailInvalid");
    if (form.openingBalance && isNaN(parseFloat(form.openingBalance)))
      e.openingBalance = t("vendorModal.validation.openingBalanceInvalid");
    if (parseFloat(form.openingBalance) < 0)
      e.openingBalance = t("vendorModal.validation.openingBalanceNegative");
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({
      ...(isEdit ? { id: existingVendor.id } : {}),
      salutation:       form.salutation,
      name:             form.name.trim(),
      additionalDetail: form.additionalDetail.trim(),
      contact:          form.contact.trim(),
      email:            form.email.trim().toLowerCase(),
      taxName:          form.taxName.trim(),
      taxNumber:        form.taxNumber.trim(),
      address:          form.address.trim(),
      openingBalance:   form.openingBalance ? parseFloat(form.openingBalance) : null,
      openingDate:      form.openingDate || null,
      balanceType:      parseFloat(form.openingBalance) > 0 ? form.balanceType : null,
    });
  };

  const showBalanceType = form.openingBalance && parseFloat(form.openingBalance) > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 16 }}
        transition={{ duration: 0.22 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Truck" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEdit
                  ? t("vendorModal.titleEdit")
                  : t("vendorModal.titleAdd")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isEdit
                  ? t("vendorModal.subtitleEdit")
                  : t("vendorModal.subtitleAdd")}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Salutation */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("vendorModal.fields.salutation")}
            </label>
            <div className="flex flex-wrap gap-2">
              {SALUTATIONS.map((s) => (
                <button
                  key={s} type="button"
                  onClick={() => set("salutation", form.salutation === s ? "" : s)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-150 ${
                    form.salutation === s
                      ? "border-primary bg-primary/10 text-primary shadow-sm"
                      : "border-border text-muted-foreground hover:border-primary/40 hover:bg-muted/50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("vendorModal.fields.vendorName")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="text" value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={t("vendorModal.placeholders.vendorName")}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Additional Detail */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("vendorModal.fields.additionalDetail")}
            </label>
            <Input
              type="text" value={form.additionalDetail}
              onChange={(e) => set("additionalDetail", e.target.value)}
              placeholder={t("vendorModal.placeholders.additionalDetail")}
            />
          </div>

          {/* Contact + Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("vendorModal.fields.contact")}
              </label>
              <Input
                type="tel" value={form.contact}
                onChange={(e) => set("contact", e.target.value)}
                placeholder={t("vendorModal.placeholders.contact")}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("vendorModal.fields.email")}
              </label>
              <Input
                type="email" value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder={t("vendorModal.placeholders.email")}
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Tax ID — two fields side by side */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("vendorModal.fields.taxId")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input
                  type="text" value={form.taxName}
                  onChange={(e) => set("taxName", e.target.value)}
                  placeholder={t("vendorModal.placeholders.taxName")}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("vendorModal.fields.taxName")}
                </p>
              </div>
              <div>
                <Input
                  type="text" value={form.taxNumber}
                  onChange={(e) => set("taxNumber", e.target.value)}
                  placeholder={t("vendorModal.placeholders.taxNumber")}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("vendorModal.fields.taxNumber")}
                </p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("vendorModal.fields.address")}
            </label>
            <textarea
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder={t("vendorModal.placeholders.address")}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
            />
          </div>

          {/* Opening Balance + Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("vendorModal.fields.openingBalance")}
              </label>
              <div className="relative">
                <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">SR</span>
                <Input
                  type="number" value={form.openingBalance}
                  onChange={(e) => set("openingBalance", e.target.value)}
                  placeholder={t("vendorModal.placeholders.openingBalance")} min="0" step="0.01"
                  className={`ps-9 ${errors.openingBalance ? "border-red-500" : ""}`}
                />
              </div>
              {errors.openingBalance && <p className="text-xs text-red-500 mt-1">{errors.openingBalance}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("vendorModal.fields.openingDate")}
              </label>
              <Input
                type="date" value={form.openingDate}
                onChange={(e) => set("openingDate", e.target.value)}
              />
            </div>
          </div>

          {/* Balance Type — only shown when balance > 0 */}
          <AnimatePresence>
            {showBalanceType && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.22 }}
                className="overflow-hidden"
              >
                <div className="p-4 rounded-xl border border-border bg-muted/30">
                  <label className="block text-sm font-medium text-foreground mb-3">
                    {t("vendorModal.fields.balanceType")}
                  </label>
                  <div className="flex gap-3">
                    {[
                      { value: "you_owe",   labelKey: "vendorModal.balanceTypes.youOweVendor",   icon: "TrendingDown", color: "text-red-500"     },
                      { value: "owes_you",  labelKey: "vendorModal.balanceTypes.vendorOwesYou",  icon: "TrendingUp",   color: "text-emerald-600" },
                    ].map((opt) => (
                      <button
                        key={opt.value} type="button"
                        onClick={() => set("balanceType", opt.value)}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-150 ${
                          form.balanceType === opt.value
                            ? "border-primary bg-primary/10 text-primary shadow-sm"
                            : "border-border text-muted-foreground hover:border-primary/40 hover:bg-muted/50"
                        }`}
                      >
                        <Icon
                          name={opt.icon} size={16}
                          className={form.balanceType === opt.value ? "text-primary" : opt.color}
                        />
                        {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-end gap-3 flex-shrink-0">
          <Button variant="outline" size="md" onClick={onClose}>
            {t("common:actions.close")}
          </Button>
          <Button variant="default" size="md" iconName={isEdit ? "Save" : "Truck"} onClick={handleSave}>
            {isEdit
              ? t("vendorModal.actions.saveChanges")
              : t("vendorModal.actions.saveAndExit")}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddVendorModal;
